import { useEffect, useState } from "react";
import { BellRing } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { allowCallPopups, autoPush, enablePush, snoozePrompt, stopAsking } from "@/lib/push";

/**
 * After sign-in: if the browser already allows notifications the device is registered quietly. If it was never asked, a
 * slim banner says why push is useful; its button opens the browser's own permission box, and once allowed the device
 * is set up with nothing more to do. "Not now" waits a week; "Don't ask again" is final for this person on this device.
 */
export function PushPrompt() {
  const { user, hasPermission } = useAuth();
  const { success, error } = useToast();
  const [ask, setAsk] = useState<"ask" | "ask-calls" | null>(null);
  const [busy, setBusy] = useState(false);
  const userId = user?.Id !== undefined ? String(user.Id) : "";

  useEffect(() => {
    if (!userId) return;
    let live = true;
    void autoPush(userId, hasPermission("call.make")).then((r) => live && setAsk(r === "none" ? null : r));
    return () => { live = false; };
  }, [userId]);

  if (!ask) return null;

  const turnOn = async () => {
    setBusy(true);
    try {
      if (ask === "ask") {
        await enablePush(userId);
        success("Notifications are on", "This device will get missed calls, new leads and approvals, even when the CRM is closed.");
      } else {
        await allowCallPopups();
        success("Notifications are on", "You'll get a pop-up when a customer calls and the CRM is in the background.");
      }
      setAsk(null);
    } catch (e: any) {
      error("Couldn't turn on notifications", e?.message || "Try again from Settings → Notifications.");
      setAsk(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div role="status" className="flex shrink-0 flex-wrap items-center gap-2.5 border-b border-primary/20 bg-accent px-4 py-2 text-[13px] text-primary">
      <BellRing className="size-4 shrink-0" aria-hidden="true" />
      <span className="min-w-[220px] flex-1">{ask === "ask" ? "Get missed calls, new leads and approvals on this device, even when the CRM is closed." : "Get a pop-up when a customer calls and the CRM is in the background."}</span>
      <button type="button" disabled={busy} onClick={turnOn} className="h-7 rounded-md bg-primary px-2.5 text-xs font-semibold text-primary-foreground disabled:opacity-60">Turn on</button>
      <button type="button" disabled={busy} onClick={() => { snoozePrompt(); setAsk(null); }} className="h-7 rounded-md px-2.5 text-xs font-semibold hover:bg-primary/10">Not now</button>
      <button type="button" disabled={busy} onClick={() => { stopAsking(userId); setAsk(null); }} className="h-7 rounded-md px-2.5 text-xs text-primary/80 hover:bg-primary/10">Don't ask again</button>
    </div>
  );
}
